import React from 'react';
import {
    ActionButton,
    MessageBar,
    MessageBarType,
    Panel,
    PanelType,
    PrimaryButton,
    Spinner,
    Stack,
    Text,
    useTheme,
} from '@fluentui/react';
import {useHistory} from 'react-router-dom';
import Badge from '../Common/Badge';
import {
    ASSISTANT_CAPABILITY_STOREFRONT_RESTYLE,
    ASSISTANT_STATUS,
    IAssistantSession,
} from '../../models/Assistant';
import {useGetAssistantSessionsQuery} from '../../store/endpoints/AssistantEndpoints';
import {useGetStorefrontsQuery} from '../../store/endpoints/StorefrontSettingsEndpoints';

/** How many recent sessions the panel lists. Enough to cover a week of building without paging. */
const SESSION_COUNT = 20;

/** Where an actionable session sends the user. The dialog there reads ?generationSession. */
const STOREFRONTS_PATH = '/settings/storefront/storefronts';

interface IAssistantSessionsPanelProps {
    isOpen: boolean;
    onDismiss: () => void;
}

type ChipColor = 'default' | 'success' | 'warning' | 'error';

/**
 * The chip a session shows, and whether it still has something for the user to do.
 *
 * "Published" is only ever the published flag, never the Completed status on its own. The worker can
 * end its turn and leave the session Completed with nothing published, and telling an operator their
 * storefront is live when it is not is the single worst thing this list could say. Such a session is
 * still waiting on a human, so it reads as review and stays actionable.
 */
export const sessionChip = (session: IAssistantSession): {text: string, color: ChipColor, actionable: boolean} => {
    switch (session.status) {
        case ASSISTANT_STATUS.PENDING:
        case ASSISTANT_STATUS.PROCESSING:
            return {text: 'Building', color: 'default', actionable: true};
        case ASSISTANT_STATUS.AWAITING_APPROVAL:
            return {text: 'Ready for review', color: 'warning', actionable: true};
        case ASSISTANT_STATUS.COMPLETED:
            return session.published === true
                ? {text: 'Published', color: 'success', actionable: false}
                : {text: 'Ready for review', color: 'warning', actionable: true};
        case ASSISTANT_STATUS.CANCELLED:
            return {text: 'Cancelled', color: 'default', actionable: false};
        case ASSISTANT_STATUS.FAILED:
            return {text: 'Failed', color: 'error', actionable: false};
        default:
            // An unrecognised status is shown verbatim rather than guessed at, and never offers an
            // action: the panel cannot promise the generation dialog knows what to do with it.
            return {text: session.status ?? 'Unknown', color: 'default', actionable: false};
    }
};

/**
 * The storefront builds this organization has run, and the way back into the ones still waiting on
 * someone.
 *
 * Deliberately not a chat. The panel lists sessions and hands off; Preview, Update, Publish and
 * Start over all live in the generation dialog on the storefronts page, which names the storefront it
 * is operating on in its own subtitle. After a build was published against the wrong store, every
 * destructive action stays in the one place that says out loud which store it means.
 */
const AssistantSessionsPanel: React.FC<IAssistantSessionsPanelProps> = (props) => {
    const theme = useTheme();
    const history = useHistory();

    // Nothing is fetched until the panel is opened, and opening it always re-reads the list: a
    // session that finished while the panel was shut must not still read as Building.
    const sessions = useGetAssistantSessionsQuery(
        {capability: ASSISTANT_CAPABILITY_STOREFRONT_RESTYLE, count: SESSION_COUNT},
        {skip: !props.isOpen, refetchOnMountOrArgChange: true});
    const storefronts = useGetStorefrontsQuery(undefined, {skip: !props.isOpen});

    // Newest first. `created` is the ordering the user thinks in (when they asked for the build);
    // lastUpdated only covers a session the API wrote without a created stamp.
    const ordered = React.useMemo(() => {
        const items = [...(sessions.data?.items ?? [])];
        items.sort((a, b) => (b.created ?? b.lastUpdated ?? '').localeCompare(a.created ?? a.lastUpdated ?? ''));
        return items;
    }, [sessions.data]);

    // The generation creates its own storefront; targetId is the fallback for a session pointed at a
    // storefront that already existed. The subUrl disambiguates near-identical names.
    const labelFor = (session: IAssistantSession): string => {
        const storefrontId = session.createdStorefrontId ?? session.targetId;
        const row = storefrontId
            ? storefronts.data?.items?.find(s => s.id === storefrontId)
            : undefined;
        if (row) return `${row.name} (${row.liveSubUrl ?? row.subUrl})`;
        return session.title || 'Storefront build';
    };

    const open = (session: IAssistantSession) => {
        if (!session.id) return;
        props.onDismiss();
        history.push(`${STOREFRONTS_PATH}?generationSession=${encodeURIComponent(session.id)}`);
    };

    const rows = ordered.map((session) => {
        const chip = sessionChip(session);
        return (
            <Stack
                key={session.id}
                tokens={{childrenGap: '0.5em'}}
                styles={{root: {
                    padding: '1em',
                    borderRadius: 6,
                    border: `1px solid ${theme.palette.neutralQuaternaryAlt}`,
                    backgroundColor: theme.palette.neutralLighterAlt,
                }}}
            >
                <Stack horizontal horizontalAlign='space-between' verticalAlign='center' tokens={{childrenGap: '0.5em'}}>
                    <Text variant='mediumPlus' styles={{root: {fontWeight: 600}}}>{labelFor(session)}</Text>
                    <Badge text={chip.text} color={chip.color} />
                </Stack>
                {session.sourceWebsiteUrl && (
                    <Text variant='small' styles={{root: {color: theme.palette.neutralSecondary}}}>
                        From {session.sourceWebsiteUrl}
                    </Text>
                )}
                {session.created && (
                    <Text variant='small' styles={{root: {color: theme.palette.neutralSecondary}}}>
                        Started {new Date(session.created).toLocaleString()}
                    </Text>
                )}
                {chip.actionable && (
                    <Stack horizontal>
                        <PrimaryButton
                            text='Open'
                            iconProps={{iconName: 'OpenInNewWindow'}}
                            onClick={() => open(session)}
                        />
                    </Stack>
                )}
            </Stack>
        );
    });

    return (
        <Panel
            isOpen={props.isOpen}
            onDismiss={props.onDismiss}
            headerText='Adaptive Intelligence'
            type={PanelType.medium}
            closeButtonAriaLabel='Close'
            isLightDismiss={true}
            isBlocking={false}
        >
            <Stack tokens={{childrenGap: '1em'}} styles={{root: {paddingTop: '1em'}}}>
                <Text variant='small' styles={{root: {color: theme.palette.neutralSecondary}}}>
                    Storefronts Adaptive Intelligence has built for this organization. Open one to
                    preview it, ask for changes, or publish it.
                </Text>

                <Stack horizontal>
                    <ActionButton
                        iconProps={{iconName: 'Refresh'}}
                        text='Refresh'
                        disabled={sessions.isFetching}
                        onClick={() => sessions.refetch()}
                    />
                </Stack>

                {sessions.isLoading && <Spinner label='Loading your storefront builds' />}

                {sessions.isError && (
                    <MessageBar messageBarType={MessageBarType.error}>
                        Your storefront builds could not be loaded. Try again in a moment.
                    </MessageBar>
                )}

                {!sessions.isLoading && !sessions.isError && rows.length === 0 && (
                    <Text>
                        Nothing here yet. Adaptive Intelligence can build a storefront from a website
                        address on the Storefronts page.
                    </Text>
                )}

                {rows}
            </Stack>
        </Panel>
    );
};

export default AssistantSessionsPanel;
